import { useEffect, useState, useCallback, useMemo } from "react";

import {
  ActionIcon,
  Box,
  Flex,
  Group,
  LoadingOverlay,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { LayoutStandard, Pagination } from "components";
import { IRootState } from "config/redux";
import { AuthorityConstants, ServiceConstants } from "constant";
import { UserUtils } from "utils";
import { useListHook } from "hooks";
import { TodoService } from "services";
import { displayClasses } from "styles";
import { AppUtils } from "utils";

import TodoDetail from "./detail/index";
import FilterSidebar from "./FilterSidebar";
import TodoItem, { TodoItemData } from "./item";
import NewTodoModal from "./new/index";
import { useTodoFilters } from "./useTodoFilters";

const { REQUEST_TODO_LIST_ID, REQUEST_TODO_CREATE_ID, REQUEST_TODO_VIEW_ID } = ServiceConstants;

const TodoListPage = () => {
  const { t } = useTranslation();
  const canEdit = UserUtils.hasAuthority(AuthorityConstants.ROLE_TODO_CUD);

  const todoListService = useSelector(
    (state: IRootState) => state.service.object[REQUEST_TODO_LIST_ID],
  );
  const todoCreateService = useSelector(
    (state: IRootState) => state.service.object[REQUEST_TODO_CREATE_ID],
  );
  const todoViewService = useSelector(
    (state: IRootState) => state.service.object[REQUEST_TODO_VIEW_ID],
  );

  // Pagination hook
  const { page, updatePageable, resetList, totalPages, refreshList, setPage } = useListHook();

  // Local state
  const [todos, setTodos] = useState<TodoItemData[]>([]);
  const [selectedTodo, setSelectedTodo] = useState<TodoItemData | null>(null);
  const [newTodoOpened, setNewTodoOpened] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>("me");
  const [loading, setLoading] = useState(true);

  // Filters hook
  const {
    searchQuery,
    setSearchQuery,
    dateFilter,
    setDateFilter,
    typeFilter,
    setTypeFilter,
    priorityFilter,
    setPriorityFilter,
    archiveFilter,
    setArchiveFilter,
    groupedTodos,
  } = useTodoFilters(todos);

  // Debounce search query for API calls
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);

  // Reset list when tab changes
  useEffect(() => {
    resetList();
  }, [activeTab]);

  // Refresh list when filters change (this triggers page reset to 0)
  useEffect(() => {
    if (page !== undefined && page !== -1) {
      refreshList();
    }
  }, [dateFilter, typeFilter, priorityFilter, debouncedSearchQuery, archiveFilter]);

  // Fetch todos when page changes
  useEffect(() => {
    if (page === undefined) return;
    if (page === -1) {
      resetList();
      return;
    }

    setLoading(true);
    setSelectedTodo(null);

    // Build API params with filters
    const params: TodoService.TodoListParams = {
      myTodo: activeTab === "me",
      archive: archiveFilter,
      pageSize: 20,
      page: page,
    };

    // Date filter
    if (dateFilter === "today") {
      params.dateFilter = "TODAY";
    } else if (dateFilter === "week") {
      params.dateFilter = "NEXT_7_DAYS";
    } else if (dateFilter === "absence") {
      params.absence = true;
    }

    // Type filter
    if (typeFilter !== "all") {
      params.type = typeFilter;
    }

    // Priority filter
    if (priorityFilter === "urgent") {
      params.urgent = true;
    } else if (priorityFilter === "important") {
      params.important = true;
    }

    // Search
    if (debouncedSearchQuery.trim()) {
      params.search = debouncedSearchQuery;
    }

    TodoService.todoList(params);
  }, [
    page,
    activeTab,
    dateFilter,
    typeFilter,
    priorityFilter,
    debouncedSearchQuery,
    archiveFilter,
  ]);

  // Handle todo list response
  useEffect(() => {
    AppUtils.resolveServiceObject(todoListService, REQUEST_TODO_LIST_ID, (data) => {
      updatePageable(data);
      if (data?.content) {
        setTodos(data.content);
      } else if (Array.isArray(data)) {
        setTodos(data);
      } else {
        setTodos([]);
      }
      setLoading(false);
    });
  }, [todoListService]);

  // Handle create response - refresh list
  useEffect(() => {
    AppUtils.resolveServiceObject(todoCreateService, REQUEST_TODO_CREATE_ID, () => {
      setNewTodoOpened(false);
      refreshList();
    });
  }, [todoCreateService]);

  // Handle single todo view response
  useEffect(() => {
    AppUtils.resolveServiceObject(todoViewService, REQUEST_TODO_VIEW_ID, (data) => {
      if (data) {
        setSelectedTodo(data);
      }
    });
  }, [todoViewService]);

  // Scroll to top when page changes
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      const scrollables = document.querySelectorAll('.mantine-ScrollArea-viewport');
      scrollables.forEach((el) => {
        if (el.scrollTop > 0) {
          el.scrollTo({ top: 0, behavior: "smooth" });
        }
      });
    },
    [setPage],
  );

  const handleSelectTodo = useCallback((todo: TodoItemData) => {
    TodoService.todoOne(todo.id);
  }, []);

  const handleSaveTodo = useCallback(
    async (updatedTodo: TodoItemData) => {
      const updateData: TodoService.TodoCreateRequest = {
        title: updatedTodo.title,
        description: updatedTodo.description,
        type: updatedTodo.type,
        timeType: updatedTodo.timeType,
        completionType: updatedTodo.completionType,
        urgent: updatedTodo.urgent,
        important: updatedTodo.important,
        archive: updatedTodo.archive ?? false,
        recurring: updatedTodo.recurring,
        recurringType: updatedTodo.recurringType,
        dueDate: updatedTodo.dueDate,
        startTime: updatedTodo.startTime,
        endTime: updatedTodo.endTime,
        clientId: updatedTodo.client?.id ?? 0,
        users: updatedTodo.users?.map((u) => ({ userId: u.id })) ?? [],
      };

      await TodoService.todoUpdate(updatedTodo.id, updateData);
      refreshList();
      setSelectedTodo(null);
    },
    [refreshList],
  );

  const handleDeleteTodo = useCallback(
    async (id: number) => {
      await TodoService.todoDelete(id);
      setSelectedTodo(null);
      refreshList();
    },
    [refreshList],
  );

  const handleCancelEdit = useCallback(() => {
    setSelectedTodo(null);
  }, []);

  const handleCompleteTodo = useCallback(
    async (id: number) => {
      await TodoService.todoMarkCompleted(id);
      refreshList();
    },
    [refreshList],
  );

  const renderTodoList = useMemo(
    () => (
      <Box pos="relative" h="calc(100vh - 260px)">
        <LoadingOverlay visible={loading} />
        <ScrollArea h="100%">
          {groupedTodos.length === 0 && !loading ? (
            <Box py="xl" ta="center">
              <Text c="dimmed" fz={14}>
                {activeTab === "me" ? "Nemate zadataka za sebe" : "Nemate zadataka za druge"}
              </Text>
            </Box>
          ) : (
            <Stack gap={0}>
              {groupedTodos.map((group, index) => (
                <Box key={group.key} mt={index > 0 ? "xl" : 0}>
                  <Group gap="md" px="xl" py="sm">
                    <Title order={4} fz={20} c={group.isOverdue ? "red" : undefined}>
                      {group.title}
                    </Title>
                    {group.subtitle && (
                      <Text fz={14} c="dimmed">
                        {group.subtitle}
                      </Text>
                    )}
                  </Group>
                  {group.todos.map((todo, idx) => (
                    <TodoItem
                      key={todo.id}
                      {...todo}
                      isLastItem={idx === group.todos.length - 1}
                      onClick={() => handleSelectTodo(todo)}
                      showCheckbox={activeTab === "me" && canEdit}
                      onComplete={handleCompleteTodo}
                    />
                  ))}
                </Box>
              ))}
            </Stack>
          )}
        </ScrollArea>
      </Box>
    ),
    [groupedTodos, loading, activeTab, handleSelectTodo, handleCompleteTodo],
  );

  return (
    <LayoutStandard title={AppUtils.composeTabTitle(t("header.todo"))} padding={0} authority={AuthorityConstants.ROLE_TODO_R}>
      <Flex h="calc(100vh - 56px)">
        <FilterSidebar
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          priorityFilter={priorityFilter}
          setPriorityFilter={setPriorityFilter}
          archiveFilter={archiveFilter}
          setArchiveFilter={setArchiveFilter}
        />

        <Box flex={2}>
          <Tabs value={activeTab} onChange={setActiveTab} variant="unstyled">
            <Tabs.List grow>
              <Tabs.Tab value="me" className={displayClasses.tabList}>
                Za mene
              </Tabs.Tab>
              <Tabs.Tab value="others" className={displayClasses.tabList}>
                Za druge
              </Tabs.Tab>
            </Tabs.List>

            <Group mt="md" px="xl">
              <TextInput
                flex={1}
                placeholder="Pretraži sve..."
                leftSection={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {canEdit && (
                <ActionIcon size="lg" color="green" onClick={() => setNewTodoOpened(true)}>
                  <IconPlus size={20} />
                </ActionIcon>
              )}
            </Group>

            <NewTodoModal opened={newTodoOpened} onClose={() => setNewTodoOpened(false)} />

            <Tabs.Panel value="me" pt="md">
              {renderTodoList}
              <Pagination value={page} onChange={handlePageChange} total={totalPages} />
            </Tabs.Panel>

            <Tabs.Panel value="others" pt="md">
              {renderTodoList}
              <Pagination value={page} onChange={handlePageChange} total={totalPages} />
            </Tabs.Panel>
          </Tabs>
        </Box>

        <Box flex={1} style={{ borderLeft: "1px solid var(--mantine-color-gray-3)" }}>
          <TodoDetail
            todo={selectedTodo}
            onSave={handleSaveTodo}
            onDelete={handleDeleteTodo}
            onCancel={handleCancelEdit}
            readOnly={!canEdit}
          />
        </Box>
      </Flex>
    </LayoutStandard>
  );
};

export default TodoListPage;
